import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiCalendar,
  FiFilter,
  FiDownload,
  FiEye,
  FiEdit,
  FiSearch,
  FiUser,
  FiBriefcase
} from 'react-icons/fi';
import { toast } from 'react-toastify';

import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  TableActions,
  PageComponent,
  Modal,
  Skeletons,
  DatePeriodFilter,
  getDateParams,
  MultiSelect
} from '../../components';

import '../CompanyManager/style.css';
import './style.css';

import ScheduleCalendar from './ScheduleCalendar';

export default function WorkSchedules() {
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // View Mode
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // Filters
  const [periodMode, setPeriodMode] = useState('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
  
  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  const [stations, setStations] = useState([]); 
  const [selectedStation, setSelectedStation] = useState('all');
  
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  const [journeys, setJourneys] = useState([]); // Store journeys centrally
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const calendarRef = useRef(null);

  // Modals
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedScheduleForStatus, setSelectedScheduleForStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  // Helper to calculate stats
  const calculateStats = (item, year, month) => {
      let workDays = 0;
      let offDays = 0;
      
      const totalDays = new Date(year, month + 1, 0).getDate();
      
      let overrides = {};
      try {
          overrides = typeof item.overrides === 'string' 
            ? JSON.parse(item.overrides || '{}') 
            : (item.overrides || {});
      } catch (e) {
          overrides = {};
      }
      
      // Normalize overrides
      const overridesMap = new Map();
      Object.entries(overrides).forEach(([k, v]) => {
          const parts = k.split('-').map(Number);
          if (parts.length === 3) {
               // Only consider overrides for the current month/year
               if (parts[0] === year && parts[1] === (month + 1)) {
                   const normKey = `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`;
                   overridesMap.set(normKey, v);
               }
          }
      });

      const scheduleName = item.schedule?.toLowerCase() || '';
      const admissionDateStr = item.original?.admission_date;
      const admissionDate = admissionDateStr ? new Date(admissionDateStr) : new Date(year, 0, 1);
      admissionDate.setHours(0, 0, 0, 0);

      for (let i = 1; i <= totalDays; i++) {
          const currentDayDate = new Date(year, month, i);
          currentDayDate.setHours(0, 0, 0, 0);
          const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
          
          let status = 'FOLGA'; // Default

          if (overridesMap.has(dateKey)) {
              status = overridesMap.get(dateKey);
          } else if (scheduleName.includes('12x36')) {
              const diffTime = currentDayDate.getTime() - admissionDate.getTime();
              const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
              if (diffDays % 2 === 0) status = 'TRAB.';
          } else if (scheduleName.includes('5x2') || scheduleName.includes('segunda a sexta') || scheduleName.includes('administrativo')) {
              const day = currentDayDate.getDay();
              if (day !== 0 && day !== 6) status = 'TRAB.';
          } else if (scheduleName.includes('6x1') || scheduleName.includes('segunda a sabado') || scheduleName.includes('segunda a sábado')) {
              const day = currentDayDate.getDay();
              if (day !== 0) status = 'TRAB.';
          } else {
               // Default fallback (Mon-Fri)
              const day = currentDayDate.getDay();
              if (day !== 0 && day !== 6) status = 'TRAB.';
          }

          if (status === 'TRAB.') workDays++;
          else offDays++;
      }
      
      return { workDays, offDays };
  };

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const [companiesRes, employeesRes] = await Promise.all([
          Api.get('/companies'),
          // Fetch employees with schedule data included for the selected period
          Api.get(`/schedules/list?year=${year}&month=${month}`),
        ]);
        
        // console.log('API Response:', employeesRes);

        const comps = companiesRes.data || [];
        setCompanies(comps);
        
        // Check if data is nested in .data (axios style) or direct
        let employeesData = employeesRes;
        if (employeesRes && employeesRes.data && Array.isArray(employeesRes.data)) {
            employeesData = employeesRes.data;
        } else if (employeesRes && Array.isArray(employeesRes)) {
            employeesData = employeesRes;
        } else {
            employeesData = [];
        }
        
        // Process stats and filters
        const allStations = [];
        const seenStationIds = new Set();
        const allEmployees = [];
        
        if (Array.isArray(employeesData)) {
            employeesData = employeesData.map(item => {
                // Calculate stats
                const stats = calculateStats(item, year, month);
                
                // Populate filters
                allEmployees.push({ id: item.employee.id, name: item.employee.name });

                if (item.station && item.station.id !== 0) {
                    if(!seenStationIds.has(item.station.id)) {
                        seenStationIds.add(item.station.id);
                        allStations.push(item.station);
                    }
                }
                
                return {
                    ...item,
                    shifts_count: stats.workDays,
                    days_off_count: stats.offDays
                };
            });
        }
        
        // No need to map manually, backend sends correct structure
        setSchedules(employeesData);
        setFilteredSchedules(employeesData);
        
        setStations(allStations);
        setEmployees(allEmployees);
        setHasLoadedOnce(true);
        
      } catch (error) {
        console.error("Error loading data:", error);
        
        // Log detailed error info if available
        if (error.response) {
            console.error("Response error data:", error.response.data);
            console.error("Response status:", error.response.status);
        }
        
        toast.error("Erro ao carregar dados. Verifique o console para mais detalhes.");
        
        // Set empty arrays to avoid UI crashes
        setSchedules([]);
        setFilteredSchedules([]);
        setHasLoadedOnce(true);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [currentDate]); // Reload when date changes

  useEffect(() => {
    // Ensure schedules is an array
    const safeSchedules = Array.isArray(schedules) ? schedules : [];
    let result = [...safeSchedules];

    if (companyFilterValues.length > 0 && !companyFilterValues.includes('all')) {
      result = result.filter(item => 
        companyFilterValues.includes(String(item.company.id))
      );
    }

    if (selectedStation !== 'all') {
      result = result.filter(item => 
        item.station.id === Number(selectedStation) || 
        (item.original.serviceStations && item.original.serviceStations.some(s => s.id === Number(selectedStation)))
      );
    }

    if (selectedEmployee !== 'all') {
      result = result.filter(item => item.employee.id === Number(selectedEmployee));
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'conferido') {
        result = result.filter(item => item.checked === true);
      } else if (statusFilter === 'nao_conferido') {
        result = result.filter(item => item.checked === false);
      }
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.employee.name.toLowerCase().includes(lower) ||
        (item.employee.role && item.employee.role.toLowerCase().includes(lower)) ||
        (item.employee.registration && item.employee.registration.toLowerCase().includes(lower))
      );
    }

    setFilteredSchedules(result);
    setCurrentPage(1); // Reset page on filter change
  }, [schedules, companyFilterValues, selectedStation, selectedEmployee, statusFilter, searchTerm]);

  function handleCompanyFilterChange(selected) {
    setCompanyFilterValues(selected);
  }

  function handleViewSchedule(schedule) {
    setSelectedSchedule(schedule);
    setIsScheduleModalOpen(true);
  }

  function handleOpenStatusModal(schedule) {
    setSelectedScheduleForStatus(schedule);
    setStatusModalOpen(true);
  }

  function handleStatusUpdate() {
    toast.success("Status atualizado com sucesso!");
    setStatusModalOpen(false);
  }

  function handleExport(schedule) {
    toast.info("Exportando...");
  }

  const columns = useMemo(() => [
    {
      id: 'employee',
      header: 'Colaborador',
      width: '2.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: row => (
        <>
          <div className="manager-table-avatar" style={{ marginRight: '0.75rem', flexShrink: 0 }}>
            {row.employee.photo ? (
              <img src={row.employee.photo} alt={row.employee.name} />
            ) : (
              <FiUser size={18} />
            )}
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{row.employee.name}</span>
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: '0.25rem',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#4b5563',
                backgroundColor: '#f3f4f6',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                width: 'fit-content',
                textTransform: 'uppercase',
                letterSpacing: '0.025em'
              }}
            >
              <FiBriefcase size={10} style={{ marginRight: '0.375rem', color: '#6b7280' }} />
              {row.employee.role}
            </div>
          </div>
        </>
      )
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: row => <span className="text-sm text-gray-600">{row.company.trade_name || row.company.corporate_name || row.company.name}</span>
    },
    {
      id: 'station',
      header: 'Posto de Serviço',
      width: '1.2fr',
      render: row => <span className="text-sm font-medium">{row.station.name?.toUpperCase()}</span>
    },
    {
      id: 'schedule',
      header: 'Jornada',
      width: '1.5fr',
      render: row => (
        <span className="manager-tag manager-tag-info">
          {row.schedule?.toUpperCase()}
        </span>
      )
    },
    {
      id: 'shifts',
      header: 'Escala',
      width: '90px',
      render: row => (
        <span className="manager-tag manager-tag-success">
          {row.shifts_count}
        </span>
      )
    },
    {
      id: 'days_off',
      header: 'Folga',
      width: '80px',
      render: row => (
        <span className="manager-tag manager-tag-danger">
          {row.days_off_count}
        </span>
      )
    },
    {
      id: 'checked',
      header: 'Conferido',
      width: '100px',
      render: row => (
        <span className={`manager-tag ${
          row.checked ? 'manager-tag-success' : 'manager-tag-warning'
        }`}>
          {row.checked ? 'SIM' : 'NÃO'}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '60px',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: row => (
        <TableActions>
          <button onClick={() => handleViewSchedule(row)}>
            <FiEye /> Ver Escala
          </button>
          <button onClick={() => handleOpenStatusModal(row)}>
            <FiEdit /> Alterar Status
          </button>
          <button onClick={() => handleExport(row)}>
            <FiDownload /> Exportar
          </button>
        </TableActions>
      )
    }
  ], []);

  const isMobile = window.innerWidth <= 768;

  // Pagination Logic
  const safeFilteredSchedules = Array.isArray(filteredSchedules) ? filteredSchedules : [];
  const totalItems = safeFilteredSchedules.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedSchedules = safeFilteredSchedules.slice(startIndex, startIndex + pageSize);

  function WorkSchedulesSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <WorkSchedulesSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiCalendar size={22} />
                </div>
                <div>
                  <h1>Escala de Trabalho</h1>
                  <p>Gerenciamento de escalas de trabalho dos colaboradores</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button variant="secondary" onClick={() => handleExport()}>
                  <FiDownload size={18} />
                  {!isMobile && 'Exportar'}
                </Button>
              </div>
            </div>

            <Card className="manager-filters-card">
        <div className="manager-filters-header">
          <div className="manager-filters-title">
            <FiFilter size={16} />
            <span>Filtros</span>
          </div>
          {schedules.length > 0 && (
            <span className="manager-filters-count">
              {filteredSchedules.length} de {schedules.length} escalas
            </span>
          )}
        </div>

        <div className="work-schedules-filters-grid">
          <div className="manager-filter-group">
            <DatePeriodFilter
              periodMode={periodMode}
              setPeriodMode={setPeriodMode}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              customRange={customPeriod}
              setCustomRange={setCustomPeriod}
            />
          </div>

          <div className="manager-filter-group">
            <label className="manager-filter-label">Empresa</label>
            <MultiSelect
              id="companyFilter"
              options={[{ value: 'all', label: 'TODAS' }, ...companies.map(c => ({
                value: String(c.id),
                label: c.trade_name || c.name
              }))]}
              value={companyFilterValues}
              onChange={handleCompanyFilterChange}
              searchable
            />
          </div>

          <div className="manager-filter-group">
            <label className="manager-filter-label">Posto de Serviço</label>
            <Select
              options={[
                { value: 'all', label: 'TODOS' },
                ...stations.map(s => ({ value: s.id, label: s.name }))
              ]}
              value={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
              searchable
            />
          </div>

          <div className="manager-filter-group">
            <label className="manager-filter-label">Colaborador</label>
            <Select
              options={[
                { value: 'all', label: 'TODOS' },
                ...employees.map(e => ({ value: e.id, label: e.name }))
              ]}
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              searchable
            />
          </div>

          <div className="manager-filter-group">
            <label className="manager-filter-label">Conferido</label>
            <Select
              options={[
                { value: 'all', label: 'TODOS' },
                { value: 'conferido', label: 'SIM' },
                { value: 'nao_conferido', label: 'NÃO' }
              ]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>

          <div className="manager-filter-group">
            <label className="manager-filter-label">Pesquisar</label>
            <Input
              icon={FiSearch}
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {isScheduleModalOpen && selectedSchedule && (
        <Modal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          title={`Visualização de Escala`}
          size="xl"
        >
          <div className="p-4" style={{ maxHeight: 'calc(80vh - 80px)', overflowY: 'auto' }}>
            <ScheduleCalendar
              ref={calendarRef}
              employee={selectedSchedule.employee}
              onClose={() => setIsScheduleModalOpen(false)}
              fullScheduleData={selectedSchedule}
            />
          </div>
          <div className="modal-actions" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.5rem', marginTop: 0 }}>
             <Button type="button" className="cancel-button" onClick={() => setIsScheduleModalOpen(false)}>
               Fechar
             </Button>
             <Button onClick={async () => {
                try {
                  if (calendarRef.current) {
                    const data = calendarRef.current.getScheduleData();
                    
                    // Save to backend
                    await Api.post('/schedules/save', data);
                    
                    toast.success('Escala conferida com sucesso!');
                    
                    // Update local state to reflect checked status AND overrides AND Recalculate stats
                     setSchedules(prevSchedules => prevSchedules.map(s => {
                       if (s.employee.id === data.employee_id) {
                           // Update the object with new overrides
                           const updatedSchedule = { ...s, checked: true, overrides: data.overrides };
                           
                           // Recalculate stats for this updated item
                           const currentYear = currentDate.getFullYear();
                           const currentMonth = currentDate.getMonth();
                           const stats = calculateStats(updatedSchedule, currentYear, currentMonth);
                           
                           return {
                               ...updatedSchedule,
                               shifts_count: stats.workDays,
                               days_off_count: stats.offDays
                           };
                       }
                       return s;
                     }));
                     
                     // Also update selectedSchedule if we keep the modal open or for immediate consistency
                     // But we are closing the modal, so updating the list is enough for next open.
                     
                     setIsScheduleModalOpen(false);
                  }
                } catch (error) {
                  console.error('Error saving schedule:', error);
                  toast.error('Erro ao salvar conferência.');
                }
             }}>
               Conferido
             </Button>
          </div>
        </Modal>
      )}

      <Table
        items={paginatedSchedules}
        columns={columns}
        totalItems={totalItems}
        page={safePage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={value => {
          setPageSize(value);
          setCurrentPage(1);
        }}
        getRowKey={row => row.id}
        emptyMessage="Nenhuma escala encontrada."
      />
      </>
        )}
      </div>

      <Modal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Alteração de Status"
        width="400px"
      >
        <div className="p-4">
          <p className="text-center mb-4 font-medium">
            - 1 colaboradores serão alteradas -
          </p>
          
          <div className="form-group">
            <label className="form-label">Novo Status</label>
            <Select
              options={[
                { value: 'conferido', label: 'CONFERIDO' },
                { value: 'pendente', label: 'PENDENTE' }
              ]}
              onChange={(e) => setNewStatus(e.target.value)}
              placeholder="SELECIONE ..."
            />
          </div>
          
          <div className="modal-actions">
             <Button variant="outline" onClick={() => setStatusModalOpen(false)}>
               Cancelar
             </Button>
             <Button onClick={handleStatusUpdate}>
               Salvar
             </Button>
          </div>
        </div>
      </Modal>
    </PageComponent>
  );
}
